import React, { useState, useRef, useEffect } from 'react';
import './otp-modal.style.scss';
import { useTranslation } from 'react-i18next';

export interface OTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (otp: string) => void;
  onResend: () => void;
  loading?: boolean;
  counter?: number;
  setCounter?: (counter: number) => void;
  resetTrigger?: boolean; 
}

export const OTPModal: React.FC<OTPModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onResend,
  loading = false,
  counter = 300,
  setCounter,
  resetTrigger = false
}) => {
  const [otpFields, setOtpFields] = useState<string[]>(new Array(4).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const {t} = useTranslation();

  useEffect(() => {
    if (isOpen && counter > 0) {
      const timer = setTimeout(() => {
        setCounter?.(counter - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [counter, isOpen, setCounter]);

  useEffect(() => {
    if (isOpen) {
      setOtpFields(new Array(4).fill(''));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setOtpFields(new Array(4).fill(''));
    }
  }, [isOpen]);

  useEffect(() => {
    if (resetTrigger) {
      setOtpFields(new Array(4).fill(''));
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [resetTrigger]);

  const handleOtpChange = (index: number, value: string) => {
    if (isNaN(Number(value))) return;

    const newOtp = [...otpFields];
    newOtp[index] = value.substring(value.length - 1);
    setOtpFields(newOtp);

    if (value && index < 3 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }

    if (value && index === 3) {
      const combinedOTP = newOtp.join('');
      if (combinedOTP.length === 4) {
        onSubmit(combinedOTP);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpFields[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleClose = () => {
    setOtpFields(new Array(4).fill(''));
    onClose();
  };

  const handleResend = () => {
    setOtpFields(new Array(4).fill(''));
    setCounter?.(300);
    onResend();
    setTimeout(() => {
      inputRefs.current[0]?.focus();
    }, 100);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="otp-modal-overlay">
      <div className="otp-modal">
        <div className="otp-modal__header pb-3">
          <h3 className="otp-modal__title">{t('ENTER_VERIFICATION_CODE')}</h3>
          <button
            className="otp-modal__close pb-2"
            onClick={handleClose}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="otp-modal__body">
          <p className="otp-modal__description">
            {t("WE_HAVE_SENT_A_4_DIGIT_VERIFICATION_CODE_TO_YOUR_EMAIL_PLEASE_ENTER_IT_BELOW_TO_CONFIRM_YOUR_BANK_DETAILS")}
          </p>

          <div className="otp-modal__inputs">
            {otpFields.map((value, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                className="otp-modal__input"
                value={value}
                onChange={e => handleOtpChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                maxLength={1}
                disabled={loading}
              />
            ))}
          </div>

          <div className="otp-modal__timer">
            <p>
              {counter > 0 ? (
                <>{t("TIME_REMAINING")}: <span className="timer-count">{formatTime(counter)}</span></>
              ) : (
                <span className="timer-expired">{t("CODE_EXPIRED")}</span>
              )}
            </p>
          </div>

          <div className="otp-modal__actions">
            {counter > 0 ? (
              <button
                type="button"
                className="otp-modal__resend"
                onClick={handleResend}
                disabled={loading}
              >
                {t("RESEND_CODE")}
              </button>
            ) : (
              <button
                type="button"
                className="otp-modal__resend otp-modal__resend--primary"
                onClick={handleResend}
                disabled={loading}
              >
                {t("RESEND_NEW_CODE")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
